import _ from 'lodash';
import { convert } from '../../mixins/functions';
import keyboardEventHelper from '../../mixins/keys';

export default {
  model: {
    prop: 'value',
    event: 'update',
  },
  mixins: [
    convert,
  ],
  props: {
    name: {
      type: String,
      default: 'Input',
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    min: {
      type: [Number, String],
      required: false,
      default: () => null,
    },
    max: {
      type: [Number, String],
      required: false,
      default: () => null,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    autocomplete: {
      type: String,
      required: false,
      default: 'off',
    },
    iconLeft: {
      type: String,
      required: false,
      default: '',
    },
    iconLeftFilled: {
      type: [Boolean, String],
      required: false,
      default: false,
    },
    iconRight: {
      type: String,
      required: false,
      default: '',
    },
    outlined: {
      type: Boolean,
      required: false,
      default: false,
    },
    rounded: {
      type: Boolean,
      required: false,
      default: false,
    },
    regular: {
      type: Boolean,
      required: false,
      default: false,
    },
    solo: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    error: {
      type: [String, Array],
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    filled: {
      type: [Boolean, String],
      required: false,
      default: false,
    },
    color: {
      type: String,
      required: false,
      default: '',
    },
    highlight: {
      type: [Boolean, String],
      required: false,
      default: false,
    },
    counter: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
    grid: {
      type: Boolean,
      default: false,
    },
    xSmall: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    paddingLarge: {
      type: Boolean,
      default: false,
    },
    // Id of an error indicator the parent renders: invalid, and only the focusable element is
    // described by it, so screen readers read it once; no own error text or outline (BaseInput,
    // BaseSelect, BaseDateTimePicker). No default, so auto-stubs of other snapshots omit it.
    externalErrorId: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  data() {
    const classDefault = _.kebabCase(this.$options.name);
    const thousandSeparator = this.convert(1000).match(/(,|\.)/g)[0] === '.' ? '.' : ',';

    return {
      classDefault,
      uniqueId: `${classDefault}-${this._uid}`,
      focused: false,
      keyboardEventHelper,
      thousandSeparator,
      commaSeparator: thousandSeparator === '.' ? ',' : '.',
    };
  },
  computed: {
    isReadOnly() {
      return this.readonly
        && !this.disabled;
    },
    isClearable() {
      return this.clearable
        && !this.disabled
        && !this.readonly;
    },
    classes() {
      const classes = this.ClassesHook ? this.ClassesHook() : {};

      return {
        [this.classDefault]: true,
        [`${this.classDefault}--focused`]: this.focused,
        [`${this.classDefault}--icon-left`]: !!this.iconLeft,
        [`${this.classDefault}--icon-left-filled`]: this.iconLeftFilled,
        [`${this.classDefault}--icon-right`]: !!this.getIconRight,
        [`${this.classDefault}--invalid`]: this.hasError,
        [`${this.classDefault}--outlined`]: this.outlined,
        [`${this.classDefault}--readonly`]: this.isReadOnly,
        [`${this.classDefault}--disabled`]: this.disabled,
        [`${this.classDefault}--rounded`]: this.rounded,
        [`${this.classDefault}--regular`]: this.isRegular && this.regular,
        [`${this.classDefault}--placeholder`]: this.placeholder,
        [`${this.classDefault}--required`]: this.isRequired,
        [`${this.classDefault}--solo`]: this.solo,
        [`${this.classDefault}--value`]: this.hasValue,
        [`${this.classDefault}--filled`]: this.getFilled,
        [`${this.classDefault}--color`]: this.color,
        [`${this.classDefault}--highlight`]: this.highlight,
        [`${this.classDefault}--label`]: this.label,
        [`${this.classDefault}--counter`]: this.counter,
        [`${this.classDefault}--min`]: this.min !== null,
        [`${this.classDefault}--max`]: this.max !== null,
        [`${this.classDefault}--clearable`]: this.isClearable,
        [`${this.classDefault}--hidden`]: this.isHidden,
        [`${this.classDefault}--x-small`]: this.xSmall,
        [`${this.classDefault}--small`]: this.small,
        ...classes,
      };
    },
    styles() {
      const styles = this.StylesHook ? this.StylesHook() : {};
      const grid = this.grid ? {
        'grid-area': this.name,
      } : {};

      let filled = '';
      if (_.isBoolean(this.getFilled)) {
        filled = 'var(--base-input--filled-color-base)';
      } else if (_.isString(this.getFilled)) {
        filled = this.getFilled.includes('#')
          ? this.getFilled
          : `var(--${this.getFilled})`;
      }

      let color = '';
      if (_.isEmpty(this.color)) {
        color = '';
      } else if (this.color.includes('#')) {
        color = this.color;
      } else {
        color = `var(--${this.color})`;
      }

      let highlight = '';
      if (_.isBoolean(this.highlight)) {
        highlight = 'var(--base-input--highlight-base)';
      } else if (_.isString(this.highlight)) {
        highlight = this.highlight.includes('#')
          ? this.highlight
          : `var(--${this.highlight})`;
      }

      let iconLeftFilled = '';
      if (_.isBoolean(this.iconLeftFilled)) {
        iconLeftFilled = 'var(--company-main-primary)';
      } else if (_.isString(this.iconLeftFilled)) {
        iconLeftFilled = this.iconLeftFilled.includes('#')
          ? this.iconLeftFilled
          : `var(--${this.iconLeftFilled})`;
      }

      return {
        ...grid,
        '--base-input--filled-color': filled,
        '--base-input--color': color,
        '--base-input--highlight': highlight,
        '--base-input--icon-left-filled': iconLeftFilled,
        ...styles,
      };
    },
    gridTemplateColumns() {
      const columns = [];
      if (this.iconLeft) {
        let width = 3;
        if (this.small) width = 3 * 0.875;
        if (this.xSmall) width = 3 * 0.75;
        columns.push(`${width}em`);
      }
      if (this.prefixes || this.currentPrefix) {
        columns.push('auto');
      }
      columns.push('1fr');
      if (this.iconRight || this.defaultIcon || !(this.isReadOnly || this.disabled)) {
        columns.push('auto');
      }
      if (this.currentUnit) {
        columns.push('auto');
      }
      return columns.join(' ');
    },
    hasError() {
      return !!this.errorMessage;
    },
    hasValue() {
      const ifStringValidCheck = this.value !== '' && this.value !== null && this.value !== 'undefined';
      const ifNumberValidCheck = _.isNumber(this.value)
        ? !Number.isNaN(this.value)
        : true;
      const ifArrayValidCheck = this.value instanceof Array
        ? this.value.length
        : true;

      switch (this.$options.name) {
        case 'BaseInput':
          return `${_.isNumber(this.value) ? this.value : (this.value || '')}`.trim() !== '';
        default:
          return ifStringValidCheck && ifNumberValidCheck && ifArrayValidCheck;
      }
    },
    isRegular() {
      return this.regular;
    },
    placeholderText() {
      return this.focused && !this.value ? this.placeholder : '';
    },
    errorMessage() {
      return Array.isArray(this.error) && this.error[0] ? this.error[0] : this.error;
    },
    // Deprecated Features
    getFilled() {
      return this.$attrs.backgroundColor || this.filled;
    },
    getIconRight() {
      return this.$attrs.icon || this.iconRight;
    },
    baseFieldId() {
      return `${this.uniqueId}-field`;
    },
    errorMessageId() {
      return `${this.uniqueId}-error`;
    },
    hintId() {
      return `${this.uniqueId}-hint`;
    },
    counterId() {
      return `${this.uniqueId}-counter`;
    },
    inputAriaDescribedby() {
      const ids = [];
      if (this.hasError) {
        ids.push(this.errorMessageId);
      } else if (this.hint) {
        ids.push(this.hintId);
      }
      if (this.counter) {
        ids.push(this.counterId);
      }

      return ids.length ? ids.join(' ') : null;
    },
    isAriaInvalid() {
      return this.hasError || !!this.externalErrorId;
    },
    fieldAriaDescribedby() {
      const ids = [this.externalErrorId, this.inputAriaDescribedby].filter(Boolean);

      return ids.length ? ids.join(' ') : null;
    },
    isRequired() {
      return !!this.required || !!(this.label && this.label.includes('*'));
    },
    sanitizedLabel() {
      return (this.label || '').replace(/\*/g, '').trim();
    },
  },
  methods: {
    emitUpdate(val) {
      this.$emit('update', val);
    },
    emitError(event, val) {
      this.$emit(`error:${event}`, val);
    },
    emitSuccess(val) {
      this.$emit('success', val);
    },
    onFocusIn(event) {
      this.focused = true;
      this.$emit('focusin', event);
    },
    onFocusOut(event) {
      this.focused = false;
      if (this.FocusOutHook) {
        this.FocusOutHook({ event });
      }
      this.$emit('focusout', event);
    },
    clear(event, args) {
      if (this.ClearHook) {
        this.ClearHook({ event, ...args });
      }
      this.emitUpdate(args.val);
    },
    onKeyDown(event, args) {
      if (this.keyboardEventHelper.isEnter(event)) {
        event.preventDefault();
        if (this.EnterHook) {
          this.EnterHook({ event, ...args });
        }
        this.$emit('enter', event);
      } else if (this.keyboardEventHelper.isBackspace(event)) {
        if (this.BackspaceHook) {
          this.BackspaceHook({ event, ...args });
        }
        this.$emit('backspace', event);
      } else {
        if (this.KeyDownHook) {
          this.KeyDownHook({ event, ...args });
        }
        this.$emit('key', event);
      }

      this.$emit('keydown', event);
    },
    getValidatedValue(val, isPaste = false) {
      let validatedValue = null;
      let commasCount = 0;
      let decimalsCount = 0;
      let numberLength = 0;

      if (this.type === 'number') {
        let number = null;
        let numberString = `${val}`.replace(/[^-?\d.,|\d+]/g, '');
        if (isPaste) {
          numberString = numberString.replaceAll(this.thousandSeparator, '');
        }

        let numberParts = [];
        if (typeof val === 'number') {
          numberParts = `${val}`.match(/(^-?\d+|\d+)/g);
        } else {
          numberParts = numberString.match(/(^-?\d+|\d+)/g);
        }

        const commasOrPoints = numberString.match(/(,|\.)/g);

        commasCount = commasOrPoints ? commasOrPoints.length : 0;

        numberLength = numberParts ? (numberParts[0]?.length || 0) : 0;

        if (numberParts && commasCount <= 1) {
          [number] = numberParts;

          const endsWithComma = `${val}`.endsWith(this.commaSeparator);

          if ((numberParts[1] || endsWithComma) && this.maxDecimals !== 0) {
            let decimals = numberParts[1] || '';
            decimalsCount = decimals.length;
            decimals = this.maxDecimals ? decimals.slice(0, this.maxDecimals) : decimals;
            number += `.${decimals}`;
            if (!endsWithComma && !decimalsCount) {
              validatedValue = parseInt(number, 10);
            } else {
              validatedValue = number;
            }
          } else if (numberString !== '') {
            validatedValue = parseInt(numberString, 10);
          }
        }
      } else {
        validatedValue = val;
      }

      return {
        type: typeof validatedValue,
        value: validatedValue,
        numberLength,
        commasCount,
        valueFormatted: typeof validatedValue === 'number' ? this.convert(validatedValue, this.maxDecimals || decimalsCount) : validatedValue,
        numberValueFormatted: typeof validatedValue === 'number' ? this.convert(validatedValue, decimalsCount).replaceAll(this.thousandSeparator, '') : validatedValue,
        decimals: decimalsCount,
      };
    },
    handleEmits(val) {
      if (this.isNumber) {
        const numberValue = val || 0;
        const numberLength = `${val || ''}`.length;

        if (this.maxLength !== null && numberLength > parseFloat(this.maxLength)) {
          this.emitError('max-length', numberValue);
        } else if (this.minLength !== null && numberLength < parseFloat(this.minLength)) {
          this.emitError('min-length', numberValue);
        } else if (this.max !== null && numberValue > parseFloat(this.max)) {
          this.emitError('max', numberValue);
        } else if (this.min !== null && numberValue < parseFloat(this.min)) {
          this.emitError('min', numberValue);
        } else {
          this.emitSuccess(val);
        }
      } else if (this.maxLength !== null && val.length > parseFloat(this.maxLength)) {
        this.emitError('max-length', val);
      } else if (this.minLength !== null && val.length < parseFloat(this.minLength)) {
        this.emitError('min-length', val);
      } else {
        this.emitSuccess(val);
      }

      this.emitUpdate(val);
    },
    isValid(val, validValues = null) {
      let valid = false;

      if (Array.isArray(validValues) && Array.isArray(val)) {
        const values = validValues.map((validValue) => (typeof validValue === 'object' ? validValue.value : validValue));
        valid = val.length ? val.every((x) => values.includes(x)) : false;
      } else {
        valid = val !== '' && val !== null && val !== 'undefined';
      }

      return valid;
    },
  },
};
